/**
 * Lightning Fast — standalone, NO-LOGIN express resume flow.
 *   upload resume + JD  ->  pay  ->  AI generates  ->  preview + download
 *
 * Uses a CLEAN axios instance (no global interceptor) so no stale auth token
 * is ever sent — the backend express endpoints are AllowAny and must not 401.
 */
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Zap, Download, FileText, Loader, ArrowLeft, Home as HomeIcon } from 'react-feather';
import { Navbar } from './Navbar';
import AIProviderToggle from './AIProviderToggle';
import TemplateSelector from './TemplateSelector';
import ClassicTemplate from './templates/ClassicTemplate';
import ModernTemplate from './templates/ModernTemplate';
import MinimalTemplate from './templates/MinimalTemplate';
import ExecutiveTemplate from './templates/ExecutiveTemplate';

const api = process.env.REACT_APP_API_URL;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const MAX_SIZE = 10 * 1024 * 1024;

// Clean axios — bypasses the global auth interceptor; only sends provider choice.
const express = axios.create({ baseURL: api });
express.interceptors.request.use((cfg) => {
  cfg.headers['X-AI-Provider'] = localStorage.getItem('aiProvider') || 'openai';
  return cfg;
});

const TEMPLATES = { modern: ModernTemplate, classic: ClassicTemplate, minimal: MinimalTemplate, executive: ExecutiveTemplate };

function ExpressInner() {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [step, setStep]           = useState('upload');   // upload | pay | generating | done
  const [resumeFile, setResume]   = useState(null);
  const [jdFile, setJdFile]       = useState(null);
  const [jdText, setJdText]       = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [resume, setResume2]      = useState(null);
  const [dropped, setDropped]     = useState([]);
  const [template, setTemplate]   = useState(localStorage.getItem('resumeTemplate') || 'modern');
  const [busy, setBusy]           = useState(false);
  const [err, setErr]             = useState('');

  const jdValid = !!jdFile || jdText.trim().length >= 100;
  const canContinue = !!resumeFile && jdValid;

  // ── dropzones ──
  const onResume = useCallback((acc, rej) => {
    if (rej.length) return toast.error('Unsupported file or over 10 MB.');
    if (acc[0]) setResume(acc[0]);
  }, []);
  const onJD = useCallback((acc, rej) => {
    if (rej.length) return toast.error('Unsupported file or over 10 MB.');
    if (acc[0]) { setJdFile(acc[0]); setJdText(''); }
  }, []);
  const DZ_OPTS = {
    maxSize: MAX_SIZE, multiple: false,
    accept: { 'application/pdf': ['.pdf'], 'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'] },
  };
  const rDz = useDropzone({ ...DZ_OPTS, onDrop: onResume });
  const jDz = useDropzone({ ...DZ_OPTS, onDrop: onJD });

  // ── step 1 -> upload files, get session ──
  const goToPay = async () => {
    setBusy(true); setErr('');
    const fd = new FormData();
    fd.append('resume_file', resumeFile);
    if (jdFile) fd.append('jd_file', jdFile);
    else fd.append('jd_file', new File([new Blob([jdText], { type: 'text/plain' })], 'jd.txt'));
    try {
      const { data } = await express.post('express/upload/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSessionId(data.session_id);
      setStep('pay');
    } catch (e) {
      const m = e?.response?.data?.error || 'Upload failed. Please try again.';
      setErr(m); toast.error(m);
    } finally { setBusy(false); }
  };

  // ── step 2 -> pay, then generate ──
  const payAndGenerate = async () => {
    if (!stripe || !elements) return;
    setBusy(true); setErr('');
    try {
      // create intent
      const { data: pi } = await express.post('express/payment-intent/', { session_id: sessionId });
      // confirm card
      const { error, paymentIntent } = await stripe.confirmCardPayment(pi.clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (error) { setErr(error.message); toast.error(error.message); setBusy(false); return; }
      if (paymentIntent.status !== 'succeeded') { setErr('Payment not completed.'); setBusy(false); return; }
      // confirm status server-side
      await express.post('express/payment-status/', { session_id: sessionId, intent_id: pi.id });
      // generate
      setStep('generating');
      const { data: gen } = await express.post('express/generate/', { session_id: sessionId });
      setResume2(gen.resume);
      setDropped(Array.isArray(gen.dropped_keywords) ? gen.dropped_keywords : []);
      setStep('done');
      toast.success('Your resume is ready!');
    } catch (e) {
      const m = e?.response?.data?.error || 'Something went wrong. Please try again.';
      setErr(m); toast.error(m); setStep('pay');
    } finally { setBusy(false); }
  };

  const download = async (fmt) => {
    setBusy(true);
    try {
      const res = await express.get(`express/download/?session_id=${sessionId}&file_format=${fmt}&template=${template}`, { responseType: 'blob' });
      const type = fmt === 'docx' ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' : 'application/pdf';
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement('a'); a.href = url; a.download = `Resume_${template}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error(`Failed to download ${fmt.toUpperCase()}.`); }
    finally { setBusy(false); }
  };

  const chooseTemplate = (id) => { setTemplate(id); localStorage.setItem('resumeTemplate', id); };
  const Tmpl = TEMPLATES[template] || ModernTemplate;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white px-4 py-10">
      <div className="max-w-4xl mx-auto">

        {/* header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
            <HomeIcon className="w-4 h-4" /> Home
          </button>
          <div className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-500" />
            <h1 className="text-xl font-bold text-gray-800">Lightning Fast Resume</h1>
          </div>
          {/* Switchable while choosing (upload/pay); locked once the result is generated */}
          <AIProviderToggle locked={step === 'generating' || step === 'done'} />
        </div>

        {/* progress dots */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {['Upload', 'Pay', 'Download'].map((label, i) => {
            const idx = { upload: 0, pay: 1, generating: 2, done: 2 }[step];
            return (
              <React.Fragment key={label}>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${idx === i ? 'bg-amber-500 text-white' : idx > i ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                  {idx > i ? '✓ ' : ''}{label}
                </span>
                {i < 2 && <span className="w-6 h-px bg-gray-300" />}
              </React.Fragment>
            );
          })}
        </div>

        {err && <div className="mb-4 text-center text-red-500 text-sm">{err}</div>}

        {/* ───── STEP: UPLOAD ───── */}
        {step === 'upload' && (
          <div className="space-y-5">
            <p className="text-center text-gray-500 text-sm">No sign-up needed. Upload, pay once, download your optimised resume.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div {...rDz.getRootProps()} className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition ${rDz.isDragActive ? 'border-amber-500 bg-amber-50' : resumeFile ? 'border-green-400 bg-green-50' : 'border-gray-300 bg-white hover:border-amber-400'}`}>
                <input {...rDz.getInputProps()} />
                <div className="text-4xl mb-2">{resumeFile ? '✅' : '📄'}</div>
                <p className="font-semibold text-gray-700 text-sm">{resumeFile ? resumeFile.name : 'Resume'}</p>
                <p className="text-xs text-gray-400 mt-1">PDF / DOCX · max 10 MB</p>
              </div>
              <div {...jDz.getRootProps()} className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition ${jDz.isDragActive ? 'border-amber-500 bg-amber-50' : jdFile ? 'border-green-400 bg-green-50' : 'border-gray-300 bg-white hover:border-amber-400'}`}>
                <input {...jDz.getInputProps()} />
                <div className="text-4xl mb-2">{jdFile ? '✅' : '📋'}</div>
                <p className="font-semibold text-gray-700 text-sm">{jdFile ? jdFile.name : 'Job Description'}</p>
                <p className="text-xs text-gray-400 mt-1">PDF / DOCX · or paste below</p>
              </div>
            </div>
            {!jdFile && (
              <div className="relative">
                <textarea rows={5} value={jdText} onChange={(e) => setJdText(e.target.value)}
                  placeholder="…or paste the job description here (min 100 characters)"
                  className={`w-full rounded-2xl border p-4 text-sm resize-none focus:outline-none focus:ring-2 ${jdText && jdText.trim().length < 100 ? 'border-orange-300 focus:ring-orange-300' : 'border-gray-300 focus:ring-amber-400'}`} />
                <span className={`absolute bottom-3 right-4 text-xs ${jdText.trim().length >= 100 ? 'text-green-600' : 'text-gray-400'}`}>{jdText.trim().length}/100</span>
              </div>
            )}
            <button onClick={goToPay} disabled={!canContinue || busy}
              className="w-full py-3.5 rounded-xl bg-amber-500 text-white font-bold hover:bg-amber-400 disabled:opacity-40 transition flex items-center justify-center gap-2">
              {busy ? <Loader className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
              Continue to Payment — $0.99
            </button>
          </div>
        )}

        {/* ───── STEP: PAY ───── */}
        {step === 'pay' && (
          <div className="max-w-md mx-auto space-y-5">
            <div className="bg-white rounded-2xl border border-amber-100 p-5">
              <div className="flex justify-between items-center mb-3">
                <span className="text-gray-600 text-sm">One-time payment</span>
                <span className="text-2xl font-bold text-gray-800">$0.99</span>
              </div>
              <p className="text-xs text-gray-400">Your AI-optimised resume is generated right after payment.</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 mb-4">
                <CardElement options={{ style: { base: { fontSize: '15px', color: '#2b2a25', fontFamily: 'EB Garamond, Georgia, serif' } } }} />
              </div>
              <button onClick={payAndGenerate} disabled={!stripe || busy}
                className="w-full py-3.5 rounded-xl bg-amber-500 text-white font-bold hover:bg-amber-400 disabled:opacity-50 transition flex items-center justify-center gap-2">
                {busy ? <><Loader className="w-5 h-5 animate-spin" /> Processing…</> : <><Zap className="w-5 h-5" /> Pay & Generate</>}
              </button>
              <p className="text-center text-xs text-gray-400 mt-3">Secure payment via Stripe · No card data stored</p>
            </div>
            <button onClick={() => setStep('upload')} className="block mx-auto text-sm text-gray-400 hover:text-gray-600">
              <ArrowLeft className="w-4 h-4 inline" /> Back to files
            </button>
          </div>
        )}

        {/* ───── STEP: GENERATING ───── */}
        {step === 'generating' && (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader className="w-12 h-12 text-amber-500 animate-spin" />
            <p className="text-gray-700 font-medium">Creating your ATS-optimised resume…</p>
            <p className="text-gray-400 text-sm">This usually takes 15–30 seconds</p>
          </div>
        )}

        {/* ───── STEP: DONE ───── */}
        {step === 'done' && resume && (
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <TemplateSelector selected={template} onChange={chooseTemplate} />
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <button onClick={() => download('pdf')} disabled={busy} className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-500 disabled:opacity-50 text-sm">
                <Download className="w-4 h-4" /> Download PDF
              </button>
              <button onClick={() => download('docx')} disabled={busy} className="flex items-center gap-2 px-5 py-2.5 bg-blue-700 text-white rounded-xl font-semibold hover:bg-blue-600 disabled:opacity-50 text-sm">
                <FileText className="w-4 h-4" /> Download Word
              </button>
            </div>

            {/* Honesty notice — keywords deliberately left out */}
            {dropped.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <p className="text-sm font-semibold text-amber-800 mb-1">
                  ✓ Kept honest — {dropped.length} keyword{dropped.length === 1 ? '' : 's'} left out
                </p>
                <p className="text-xs text-gray-600 mb-2">
                  We didn't add these because your resume doesn't yet support them — claiming them could backfire in an interview. If you genuinely have any, add the experience and regenerate.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {dropped.map((k, i) => (
                    <span key={i} className="text-xs bg-white border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full">{k}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white shadow-lg rounded-2xl overflow-auto border border-gray-100">
              <Tmpl resume={resume} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function LightningFast() {
  return (
    <>
      <Navbar />
      <Elements stripe={stripePromise}>
        <ExpressInner />
      </Elements>
    </>
  );
}
